import { defineStore } from 'pinia'
import { computed, ref, markRaw } from 'vue'
import { useRouter } from '#app'
import {
  panelRegistry,
  type PanelKey,
} from '#build/panels.generated'

export type PanelStatus = 'mounting' | 'active' | 'backgrounded' | 'closing' | 'closed' | 'future'

export interface PanelInstance {
  id: string
  key: PanelKey
  props: Record<string, unknown>
  hash: string
  status: PanelStatus
  portal: string
  extension: string
  shouldDismiss: (() => boolean) | null
  willDismiss: (() => void) | null
  didDismiss: (() => void) | null
  didAttemptToDismiss: (() => void) | null
  resolveClose: ((v: boolean) => void) | null
}

function uuid(): string {
  let dt = new Date().getTime()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (dt + Math.random() * 16) % 16 | 0
    dt = Math.floor(dt / 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function encodeHashValue(v: unknown): string {
  if (v === null || v === undefined) return ''
  return encodeURIComponent(String(v))
}

export function buildHash(key: string, props: Record<string, unknown> = {}): string {
  // Substitute dynamic segments like [id] with values from props.
  let path = key
  const used = new Set<string>()
  path = path.replace(/\[(\w+)\]/g, (_, name) => {
    used.add(name)
    return encodeHashValue(props[name]) || `[${name}]`
  })
  // Remaining props become query string.
  const queryEntries = Object.entries(props).filter(
    ([k, v]) => !used.has(k) && v !== undefined && v !== null && isPrimitive(v),
  )
  const query = queryEntries.length
    ? '?' + queryEntries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeHashValue(v)}`).join('&')
    : ''
  return `#${path}${query}`
}

function isPrimitive(v: unknown): v is string | number | boolean {
  const t = typeof v
  return t === 'string' || t === 'number' || t === 'boolean'
}

export interface HashParseResult {
  key: PanelKey
  props: Record<string, unknown>
}

// Parse a new-format hash like `#customer/edit?id=123` or `#customer/course/abc123`
export function parseHash(rawHash: string): HashParseResult | null {
  if (!rawHash) return null
  const stripped = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash
  const [path, queryString = ''] = stripped.split('?')
  if (!path) return null
  const match = findRegistryMatch(path)
  if (!match) return null
  const query = new URLSearchParams(queryString)
  const props: Record<string, unknown> = { ...match.params }
  for (const [k, v] of query) props[k] = v
  return { key: match.key, props }
}

function parseQueryToProps(key: PanelKey, queryString: string, extra: Record<string, unknown>): HashParseResult {
  const query = new URLSearchParams(queryString)
  const props: Record<string, unknown> = { ...extra }
  for (const [k, v] of query) props[k] = v
  return { key, props }
}

interface RegistryMatch {
  key: PanelKey
  params: Record<string, string>
}

// Match a concrete path like `customer/course/abc123` to a registry key
// like `customer/course/[id]`, extracting `{ id: 'abc123' }`.
export function findRegistryMatch(path: string): RegistryMatch | null {
  if (path in panelRegistry) return { key: path as PanelKey, params: {} }
  const segments = path.split('/')
  for (const registryKey of Object.keys(panelRegistry)) {
    const patternSegments = registryKey.split('/')
    if (patternSegments.length !== segments.length) continue
    const params: Record<string, string> = {}
    let ok = true
    for (let i = 0; i < patternSegments.length; i++) {
      const p = patternSegments[i]
      const s = segments[i]
      const dyn = p.match(/^\[(\w+)\]$/)
      if (dyn) {
        params[dyn[1]] = decodeURIComponent(s)
      } else if (p !== s) {
        ok = false
        break
      }
    }
    if (ok) return { key: registryKey as PanelKey, params }
  }
  return null
}

export const usePanelStore = defineStore('panel', () => {
  const router = useRouter()

  const stack = ref<PanelInstance[]>([])
  const lastKnownStateKey = ref<number | null>(null)

  const activePanels = computed(() =>
    stack.value.filter((p) => p.status === 'active' || p.status === 'backgrounded'),
  )

  const top = computed<PanelInstance | undefined>(() => {
    const a = activePanels.value
    return a[a.length - 1]
  })

  function getComponentFor(key: PanelKey) {
    const entry = panelRegistry[key]
    if (!entry) return null
    return markRaw(entry.loader)
  }

  function buildInstance(key: PanelKey, props: Record<string, unknown>): PanelInstance {
    const id = uuid()
    return {
      id,
      key,
      props,
      hash: buildHash(key, props),
      status: 'mounting',
      portal: `portal-${id}`,
      extension: `extension-${id}`,
      shouldDismiss: null,
      willDismiss: null,
      didDismiss: null,
      didAttemptToDismiss: null,
      resolveClose: null,
    }
  }

  function open(key: PanelKey, props: Record<string, unknown> = {}): boolean {
    if (!(key in panelRegistry)) {
      console.error(`[panel] Unknown panel key "${key}". Available:`, Object.keys(panelRegistry))
      return false
    }
    const instance = buildInstance(key, props)
    // Demote previous top and promote this one synchronously so rendering doesn't
    // depend on router.push resolving — its promise can reject via scrollBehavior
    // on hashes that aren't valid CSS selectors (e.g. "#customer/edit?id=1").
    const prev = top.value
    if (prev) prev.status = 'backgrounded'
    instance.status = 'active'
    stack.value.push(instance)

    const currentRoute = router.currentRoute.value
    router.push({
      path: currentRoute.path,
      query: currentRoute.query,
      hash: instance.hash,
      state: { panelInstanceId: instance.id },
    } as any).catch(() => {}).finally(() => {
      lastKnownStateKey.value = Number((history.state as any)?.key ?? 0)
    })
    return true
  }

  async function close(instance?: PanelInstance, force: unknown = false): Promise<boolean> {
    const target = instance ?? top.value
    if (!target) return false

    return new Promise<boolean>((resolve) => {
      target.resolveClose = resolve

      // Only strict `true` is a force-close — callers often `@click="close"` which
      // passes the MouseEvent as the first arg, and that must NOT bypass shouldDismiss.
      const forced = force === true
      if (!forced && target.shouldDismiss && !target.shouldDismiss()) {
        target.didAttemptToDismiss?.()
        resolve(false)
        target.resolveClose = null
        return
      }
      target.willDismiss?.()
      target.status = 'closing'

      // Navigate back. If we are the only panel, drop the hash; otherwise
      // expose the next panel's hash below us in the stack.
      const below = findBelow(target)
      if (below) {
        const currentRoute = router.currentRoute.value
        router.push({
          path: currentRoute.path,
          query: currentRoute.query,
          hash: below.hash,
          state: { panelInstanceId: below.id },
        } as any).then(() => {
          finalizeClose(target, resolve)
        })
      } else {
        const currentRoute = router.currentRoute.value
        router.push({ path: currentRoute.path, query: currentRoute.query, hash: '' } as any).then(() => {
          finalizeClose(target, resolve)
        })
      }
    })
  }

  function findBelow(target: PanelInstance): PanelInstance | undefined {
    const a = activePanels.value
    const idx = a.indexOf(target)
    if (idx <= 0) return undefined
    return a[idx - 1]
  }

  function finalizeClose(target: PanelInstance, resolve: (v: boolean) => void) {
    target.status = 'closed'
    target.didDismiss?.()
    // Promote new top
    const next = top.value
    if (next && next.status === 'backgrounded') next.status = 'active'
    resolve(true)
    target.resolveClose = null
  }

  function replace(newKey: PanelKey, newProps: Record<string, unknown> = {}): void {
    const target = top.value
    if (!target) return
    if (!(newKey in panelRegistry)) {
      console.error(`[panel] Unknown panel key "${newKey}"`)
      return
    }
    target.key = newKey
    target.props = newProps
    target.hash = buildHash(newKey, newProps)
    const currentRoute = router.currentRoute.value
    router.replace({
      path: currentRoute.path,
      query: currentRoute.query,
      hash: target.hash,
      state: { panelInstanceId: target.id },
    } as any)
  }

  async function popTo(targetIndex: number): Promise<boolean> {
    const a = activePanels.value
    let i = a.length - 1
    while (i > targetIndex) {
      const p = a[i]
      const ok = await close(p)
      if (!ok) return false
      i--
    }
    return true
  }

  const popToRoot = () => popTo(0)
  const popToClear = () => popTo(-1)

  function findById(id: string) {
    return stack.value.find((p) => p.id === id)
  }

  async function onPopState(event: PopStateEvent): Promise<void> {
    const state: any = event.state
    const newId: string | undefined = state?.panelInstanceId
    const currentTop = top.value

    // Unsaved changes guard: block browser-back with shouldDismiss
    if (currentTop && currentTop.shouldDismiss && !currentTop.shouldDismiss()) {
      // Re-assert current URL without creating new entry
      const currentRoute = router.currentRoute.value
      router.replace({
        path: currentRoute.path,
        query: currentRoute.query,
        hash: currentTop.hash,
        state: { panelInstanceId: currentTop.id },
      } as any)
      currentTop.didAttemptToDismiss?.()
      return
    }

    if (!newId) {
      // Back past all panels → close everything that is active
      for (const p of [...activePanels.value].reverse()) {
        p.status = 'closed'
        p.didDismiss?.()
      }
      return
    }

    const targetInstance = findById(newId)
    if (!targetInstance) {
      // Stale session (post-refresh back-stack). Close current active panels.
      for (const p of [...activePanels.value].reverse()) {
        p.status = 'closed'
        p.didDismiss?.()
      }
      return
    }

    // Walk the stack: close anything above target, reactivate target.
    const idx = stack.value.indexOf(targetInstance)
    for (let i = stack.value.length - 1; i > idx; i--) {
      const p = stack.value[i]
      if (p.status === 'active' || p.status === 'backgrounded') {
        p.status = 'future'
      }
    }
    targetInstance.status = 'active'
  }

  // Mount-time restore from initial URL hash.
  async function restoreFromHash(rawHash: string): Promise<boolean> {
    if (!rawHash) return false
    const parsed = parseHash(rawHash)
    if (!parsed) return false
    // Open + let the open() call set a fresh instance id in history.state
    return open(parsed.key, parsed.props)
  }

  return {
    stack,
    activePanels,
    top,
    open,
    close,
    replace,
    popTo,
    popToRoot,
    popToClear,
    onPopState,
    restoreFromHash,
    findById,
    getComponentFor,
    buildHash,
  }
})
